import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Plus,
  Calendar,
  ArrowUpRight,
  Trash2,
  Tag,
  X,
  ChevronDown,
  Filter,
  Check,
  BadgeCheck,
  XCircle,
  Hourglass,
  Zap,
} from 'lucide-react';
import {
  Card,
  CardContent,
  Button,
  Input,
  Select,
  Badge,
  EmptyState,
  Avatar,
  Modal,
  Pagination,
} from '../components/ui';
import { blogsApi } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { formatDateTime } from '../lib/utils';
import toast from 'react-hot-toast';
import type { BlogJobStatusResponse, JobStatus } from '../types';

// ─── Status Config ─────────────────────────────────────────────────────────────
const statusConfig: Record<
  JobStatus,
  { label: string; variant: 'warning' | 'info' | 'success' | 'danger'; icon: React.ReactNode }
> = {
  pending:    { label: 'Pending',    variant: 'warning', icon: <Clock className="h-3.5 w-3.5" /> },
  processing: { label: 'Processing', variant: 'info',    icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
  completed:  { label: 'Completed',  variant: 'success', icon: <CheckCircle className="h-3.5 w-3.5" /> },
  failed:     { label: 'Failed',     variant: 'danger',  icon: <AlertCircle className="h-3.5 w-3.5" /> },
  retrying:   { label: 'Retrying',   variant: 'warning', icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
};

const getStatusConfig = (status: string) => {
  if (status.startsWith('retrying')) {
    return { label: status, variant: 'warning' as const, icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> };
  }
  return statusConfig[status as JobStatus] ?? statusConfig['pending'];
};

const statusFilterOptions = [
  { value: 'all',        label: 'All Status'  },
  { value: 'pending',    label: 'Pending'     },
  { value: 'processing', label: 'Processing'  },
  { value: 'completed',  label: 'Completed'   },
  { value: 'failed',     label: 'Failed'      },
];

// ─── Keyword Dropdown ──────────────────────────────────────────────────────────
const KeywordDropdown: React.FC<{
  keywords: string[];
  selected: string[];
  onToggle: (kw: string) => void;
  onClearAll: () => void;
}> = ({ keywords, selected, onToggle, onClearAll }) => {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref                 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = keywords.filter((kw) =>
    kw.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-2 px-4 py-2 h-[42px] rounded-xl border text-sm font-medium transition-all whitespace-nowrap ${
          selected.length > 0
            ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-400'
            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
        }`}
      >
        <Tag className="h-4 w-4" />
        Keywords
        {selected.length > 0 && (
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-600 text-white text-xs font-bold">
            {selected.length}
          </span>
        )}
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl z-50 overflow-hidden"
          >
            {/* Search */}
            <div className="p-3 border-b border-gray-100 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search keywords..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                />
              </div>
            </div>

            {/* List */}
            <div className="max-h-52 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">No keywords found</p>
              ) : (
                filtered.map((kw) => {
                  const isActive = selected.includes(kw);
                  return (
                    <button
                      key={kw}
                      onClick={() => onToggle(kw)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                        isActive
                          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60'
                      }`}
                    >
                      <span>{kw}</span>
                      {isActive && <Check className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />}
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            {selected.length > 0 && (
              <div className="p-2.5 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => { onClearAll(); setOpen(false); }}
                  className="w-full text-xs text-center text-gray-400 hover:text-red-500 dark:hover:text-red-400 py-1 transition-colors"
                >
                  Clear all ({selected.length} selected)
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Blog Card ─────────────────────────────────────────────────────────────────
const BlogCard: React.FC<{
  job: BlogJobStatusResponse;
  index: number;
  userName: string;
  selectedKeywords: string[];
  onDelete: (jobId: number, title: string) => void;
}> = ({ job, index, userName, selectedKeywords, onDelete }) => {
  const status = getStatusConfig(job.status);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card hover padding="none" className="group overflow-hidden h-full">
        <div className={`h-1 ${
          job.status === 'completed'             ? 'bg-green-500'  :
          job.status === 'processing'            ? 'bg-blue-500'   :
          job.status === 'pending'               ? 'bg-yellow-500' :
          job.status.startsWith('retrying')      ? 'bg-orange-400' :
          'bg-red-500'
        }`} />

        <div className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={status.variant} icon={status.icon}>{status.label}</Badge>
              {job.status === 'completed' && job.approval_status === 'approved' && (
                <Badge variant="success" icon={<BadgeCheck className="h-3.5 w-3.5" />} size="sm">Approved</Badge>
              )}
              {job.status === 'completed' && job.approval_status === 'rejected' && (
                <Badge variant="danger" icon={<XCircle className="h-3.5 w-3.5" />} size="sm">Rejected</Badge>
              )}
              {job.status === 'completed' && job.approval_status === 'pending_review' && (
                <Badge variant="warning" icon={<Hourglass className="h-3.5 w-3.5" />} size="sm">Pending Review</Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(job.job_id, job.selected_title || 'Untitled');
                }}
                className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-all"
              >
                <Trash2 className="h-4 w-4" />
              </button>
              <Link to={`/blogs/${job.job_id}`}>
                <ArrowUpRight className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            </div>
          </div>

          <Link to={`/blogs/${job.job_id}`}>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
              {job.selected_title || 'Untitled'}
            </h3>
          </Link>

          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-3">
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {formatDateTime(job.created_at)}
            </span>
          </div>

          {/* Keywords — highlight active filter matches */}
          {job.keywords && job.keywords.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {job.keywords.slice(0, 4).map((kw) => {
                const isActive = selectedKeywords.some(
                  (s) => s.toLowerCase().replace(/[-_]/g, ' ').trim() ===
                         kw.toLowerCase().replace(/[-_]/g, ' ').trim()
                );
                return (
                  <Badge key={kw} variant={isActive ? 'primary' : 'default'} size="sm">
                    {kw}
                  </Badge>
                );
              })}
              {job.keywords.length > 4 && (
                <span className="text-xs text-gray-400 dark:text-gray-500 self-center">
                  +{job.keywords.length - 4}
                </span>
              )}
            </div>
          )}

          <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3 flex-wrap">
              {job.result && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {job.result.word_count.toLocaleString()} words
                </span>
              )}
              {job.token_usage && (
                <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                  <Zap className="h-3 w-3 text-yellow-500" />
                  <span className="text-sky-600 dark:text-sky-400">
                    In: {job.token_usage.input_tokens >= 1000
                      ? `${(job.token_usage.input_tokens / 1000).toFixed(1)}K`
                      : job.token_usage.input_tokens}
                  </span>
                  <span className="text-gray-300 dark:text-gray-600">/</span>
                  <span className="text-purple-600 dark:text-purple-400">
                    Out: {job.token_usage.output_tokens >= 1000
                      ? `${(job.token_usage.output_tokens / 1000).toFixed(1)}K`
                      : job.token_usage.output_tokens}
                  </span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Avatar name={userName} size="sm" />
              <span className="text-xs text-gray-500 dark:text-gray-400">{userName}</span>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export const BlogList: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery]           = useState('');
  const [statusFilter, setStatusFilter]         = useState('all');
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [deleteTarget, setDeleteTarget]         = useState<{ id: number; title: string } | null>(null);
  const [currentPage, setCurrentPage]           = useState(1);
  const queryClient = useQueryClient();
  const PAGE_SIZE = 10;

  const { data: keywordsData } = useQuery({
    queryKey: ['keywords'],
    queryFn: blogsApi.getKeywords,
    staleTime: 60_000,
  });
  const allKeywords = keywordsData?.keywords ?? [];

  const { data: jobsData, isLoading } = useQuery({
    queryKey: ['jobs', selectedKeywords, currentPage],
    queryFn: () => blogsApi.getJobs(currentPage, PAGE_SIZE, selectedKeywords.length > 0 ? selectedKeywords : undefined),
    refetchInterval: 10000,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const jobs       = jobsData?.items ?? [];
  const totalPages = jobsData?.total_pages ?? 1;
  const totalJobs  = jobsData?.total ?? 0;

  const deleteMutation = useMutation({
    mutationFn: (blogId: number) => blogsApi.deleteBlog(blogId),
    onSuccess: () => {
      toast.success('Blog deleted successfully');
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to delete blog');
    },
  });

  const userName = user?.first_name && user?.last_name
    ? `${user.first_name} ${user.last_name}`
    : user?.first_name || user?.email?.split('@')[0] || 'User';

  const toggleKeyword = (kw: string) => {
    setSelectedKeywords((prev) =>
      prev.includes(kw) ? prev.filter((k) => k !== kw) : [...prev, kw]
    );
    setCurrentPage(1);
  };

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch =
      searchQuery === '' ||
      (job.selected_title || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total:     totalJobs,
    completed: jobs.filter((j) => j.status === 'completed').length,
  };

  const hasActiveFilters = selectedKeywords.length > 0 || statusFilter !== 'all' || searchQuery !== '';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Blogs</h1>
          <p className="text-gray-500 dark:text-gray-400">
            {stats.total} blogs total, {stats.completed} completed
          </p>
        </div>
        <Link to="/generate">
          <Button leftIcon={<Plus className="h-4 w-4" />}>Generate New Blog</Button>
        </Link>
      </div>

      {/* ── Filter Card ── */}
      <Card>
        <CardContent className="p-4 space-y-3">
          {/* Row 1: Search + Keyword dropdown + Status */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1">
              <Input
                placeholder="Search by title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                leftIcon={<Search className="h-5 w-5" />}
              />
            </div>
            <KeywordDropdown
              keywords={allKeywords}
              selected={selectedKeywords}
              onToggle={toggleKeyword}
              onClearAll={() => setSelectedKeywords([])}
            />
            <div className="w-full md:w-44">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={statusFilterOptions}
              />
            </div>
          </div>

          {/* Row 2: Selected keyword chips (visible only when keywords are selected) */}
          <AnimatePresence>
            {selectedKeywords.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="flex flex-wrap items-center gap-2 pt-1"
              >
                <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                  <Filter className="h-3 w-3" /> Filtered by:
                </span>
                {selectedKeywords.map((kw) => (
                  <motion.button
                    key={kw}
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.85 }}
                    onClick={() => toggleKeyword(kw)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-xs font-medium hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
                  >
                    {kw}
                    <X className="h-3 w-3" />
                  </motion.button>
                ))}
                <button
                  onClick={() => setSelectedKeywords([])}
                  className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors ml-1"
                >
                  Clear all
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* ── Result count ── */}
      <AnimatePresence>
        {hasActiveFilters && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="flex items-center justify-between px-1"
          >
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing{' '}
              <span className="font-semibold text-gray-900 dark:text-white">{filteredJobs.length}</span>
              {' '}of {stats.total} blogs
            </p>
            <button
              onClick={() => { setSearchQuery(''); setStatusFilter('all'); setSelectedKeywords([]); setCurrentPage(1); }}
              className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 flex items-center gap-1 transition-colors"
            >
              <X className="h-3.5 w-3.5" /> Reset all filters
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Blog Grid ── */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} padding="none" className="animate-pulse">
              <div className="h-1 bg-gray-200 dark:bg-gray-700" />
              <div className="p-5 space-y-3">
                <div className="h-5 w-20 skeleton rounded" />
                <div className="h-6 w-full skeleton rounded" />
                <div className="h-4 w-3/4 skeleton rounded" />
                <div className="flex gap-2">
                  <div className="h-5 w-16 skeleton rounded-full" />
                  <div className="h-5 w-16 skeleton rounded-full" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : filteredJobs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredJobs.map((job, index) => (
            <BlogCard
              key={job.job_id}
              job={job}
              index={index}
              userName={userName}
              selectedKeywords={selectedKeywords}
              onDelete={(id, title) => setDeleteTarget({ id, title })}
            />
          ))}
        </div>
      ) : jobs.length > 0 ? (
        <EmptyState
          title="No matching blogs"
          description="Try adjusting your search, status, or keyword filters"
          icon={<Search className="h-8 w-8" />}
          action={hasActiveFilters ? {
            label: 'Reset filters',
            onClick: () => { setSearchQuery(''); setStatusFilter('all'); setSelectedKeywords([]); },
          } : undefined}
        />
      ) : (
        <EmptyState
          title="No blogs yet"
          description="Start by generating your first AI-powered blog post"
          action={{ label: 'Generate Blog', onClick: () => navigate('/generate') }}
        />
      )}

      {/* ── Pagination ── */}
      {!isLoading && totalJobs > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          total={totalJobs}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
          isLoading={isLoading}
        />
      )}

      {/* ── Delete Modal ── */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Blog"
        description={`Are you sure you want to delete "${deleteTarget?.title}"? This action cannot be undone.`}
        size="sm"
      >
        <div className="flex gap-3 justify-end pt-2">
          <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            variant="danger"
            leftIcon={<Trash2 className="h-4 w-4" />}
            isLoading={deleteMutation.isPending}
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};
